import { faArrowDownToBracket } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  CompletedIcon,
  ExternalLinkIcon,
  Flex,
  ListItem,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
  TextButton,
  TextLink,
  Toast,
  Tooltip,
} from '@modusign/design-system';
import FileSaver from 'file-saver';
import { QRCodeCanvas } from 'qrcode.react';
import { ReactNode, useState } from 'react';
import { useCopyToClipboard } from 'react-use';

import TabButton from './TabButton';

function createaIframeHtml(src: string) {
  return `<iframe style="border: 1px solid rgba(0, 0, 0, 0.1);" width="800" height="800" src="${src}" allowfullscreen></iframe>`;
}

interface ContractLinkShareModalProps {
  contractLinkId: string;
  contractLinkTitle: string;
  isOpen: boolean;
  variant: 'BASIC' | 'AFTER_CREATE';
  onClose: () => void;
}

function ContractLinkShareModal({
  contractLinkId,
  contractLinkTitle,
  isOpen,
  variant,
  onClose,
}: ContractLinkShareModalProps) {
  const [tab, setTab] = useState<'URL' | 'EMBED_CODE'>('URL');
  const copy = useCopyToClipboard()[1];

  const getShareLink = (shareType: 'URL' | 'EMBED_CODE' | 'QR') => {
    return `${window.location.origin}/link/${contractLinkId}/authentication?shareType=${shareType}`;
  };

  let content = getShareLink('URL');
  if (tab === 'EMBED_CODE') {
    content = createaIframeHtml(getShareLink('EMBED_CODE'));
  }

  if (!isOpen) {
    return null;
  }

  return (
    <Modal className="tagContractLinkShareModal" isOpen={isOpen} maxWidth="xs" onClose={onClose}>
      {variant === 'AFTER_CREATE' ? (
        <ModalTitle icon={<CompletedIcon color="iconPositiveBase" />}>링크 만들기 완료</ModalTitle>
      ) : (
        <ModalTitle>링크 공유</ModalTitle>
      )}
      <ModalContent>
        {variant === 'AFTER_CREATE' && (
          <Text textAlign="center">원하는 방법으로 서명을 요청해 보세요.</Text>
        )}
        <Box>
          <Flex
            bg="buttonBackgroundNeutralSlightlyDark"
            border="1px solid"
            borderColor="buttonBackgroundNeutralSlightlyDark"
            borderRadius="8px"
            gap="3px"
            mt={7}
            p="3px"
            role="tablist"
          >
            <TabButton active={tab === 'URL'} onClick={() => setTab('URL')}>
              URL
            </TabButton>
            <TabButton active={tab === 'EMBED_CODE'} onClick={() => setTab('EMBED_CODE')}>
              Embed Code
            </TabButton>
          </Flex>
          <div role="tabpanel">
            <Flex
              aria-label={tab === 'URL' ? '공유링크 복사하기' : '임베드 코드 복사하기'}
              border="1px solid"
              borderColor="boxBorderNeutralBase"
              mt={3}
              p={6}
            >
              <Tooltip content="복사하기" placement="bottom">
                <Text
                  as="button"
                  sx={{
                    flex: 1,
                    '&:hover': {
                      textDecoration: 'underline',
                      textUnderlineOffset: '0.2em',
                    },
                  }}
                  textAlign="left"
                  type="button"
                  onClick={() => {
                    copy(content);
                    Toast.success({
                      description:
                        tab === 'URL' ? 'URL이 복사되었습니다.' : 'Embed Code가 복사되었습니다.',
                    });
                  }}
                >
                  {content}
                </Text>
              </Tooltip>

              {tab === 'URL' && (
                <Tooltip content="새 창에서 열기" placement="bottom">
                  <Box
                    alignSelf="start"
                    as="a"
                    flexBasis={20}
                    href={content}
                    rel="noreferrer"
                    target="_blank"
                    textAlign="right"
                  >
                    <ExternalLinkIcon color="iconNeutralSlightlyDark" variant="line" />
                  </Box>
                </Tooltip>
              )}
            </Flex>
            {tab === 'URL' && (
              <Flex alignItems="center" flexDirection="column" mt={4} p={6}>
                <QRCorners>
                  {/* <QRCodeCanvas /> 에서 ref props를 지원하지 않아서 id를 지정합니다. */}
                  <QRCodeCanvas
                    id="contract-link-qr-canvas"
                    size={144}
                    style={{ display: 'block' }}
                    value={getShareLink('QR')}
                  />
                </QRCorners>
                <TextButton
                  className="tagDownloadContractLinkQRCode"
                  mt={7}
                  onClick={() => {
                    const canvas = document.getElementById(
                      'contract-link-qr-canvas'
                    ) as HTMLCanvasElement | null;
                    if (!canvas) return;

                    FileSaver.saveAs(
                      canvas.toDataURL('image/png'),
                      `링크서명 QRCode(${contractLinkTitle}).png`
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faArrowDownToBracket} style={{ marginRight: '2px' }} />
                  QR코드 다운로드
                </TextButton>
              </Flex>
            )}
            {tab === 'EMBED_CODE' && (
              <Box
                as="ul"
                bg="boxBackgroundNeutralBase"
                border="1px solid"
                borderColor="boxBorderNeutralBase"
                mt={3}
                px={6}
                py={7}
              >
                <ListItem sx={{ textAlign: 'left' }} variant="square">
                  <Text>웹페이지에 해당 콘텐츠를 삽입해 이용할 수 있습니다.</Text>
                </ListItem>
                <ListItem mt={2} sx={{ textAlign: 'left' }} variant="square">
                  <Text>
                    Embed Code 사용 방법은{' '}
                    <TextLink
                      href="https://support.modusign.co.kr/hc/ko/articles/4487432505241"
                      target="_blank"
                    >
                      도움말
                    </TextLink>
                    을 참고해 주세요.
                  </Text>
                </ListItem>
              </Box>
            )}
          </div>
        </Box>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onClose}>확인</Button>
      </ModalFooter>
    </Modal>
  );
}

export default ContractLinkShareModal;

interface QRCornersProps {
  children: ReactNode;
}

function QRCorners({ children }: QRCornersProps) {
  return (
    <Box p={6} position="relative">
      {[0, 1, 2, 3].map((i) => (
        <Box
          borderColor="boxBorderPrimaryDark"
          borderStyle="solid"
          borderWidth="4px 4px 0 0"
          height="18px"
          key={i}
          left={(i + 1) % 4 < 2 ? 0 : 'calc(100% - 18px)'}
          position="absolute"
          sx={{ transform: `rotate(${(i + 3) * 0.25}turn)` }}
          top={i < 2 ? 0 : 'calc(100% - 18px)'}
          width="18px"
        />
      ))}
      {children}
    </Box>
  );
}
